import config from '../config'

export interface RawSummary {
  has_permission: boolean
  has_data: boolean
  total_files: number
  manual_files: number
  auto_files: number
  recent_7d_uploads: number
  available_years: number[]
  selected_year?: number | null
}

export interface LabelSummary {
  has_permission: boolean
  has_data: boolean
  project_count: number
  version_count: number
  total_labels: number
  matched_labels: number
  unmatched_labels: number
  recent_7d_uploads: number
  task_type_distribution: Record<string, number>
}

export interface DatasetSummary {
  has_permission: boolean
  has_data: boolean
  project_count: number
  dataset_count: number
  job_status_counts: Record<string, number>
  ready_job_count: number
  recent_7d_datasets: number
}

export interface TrainingSummary {
  has_permission: boolean
  has_data: boolean
  scope: 'personal' | 'team' | 'org'
  total_runs: number
  completed_runs: number
  failed_runs: number
  recent_7d_runs: number
}

export interface DeploymentSummary {
  has_permission: boolean
  has_data: boolean
  scope: 'personal' | 'team' | 'org'
  total_deployments: number
  finished_deployments: number
  recent_7d_deployments: number
}

export interface PipelineSummary {
  mode: 'cumulative' | 'latest'
  raw_history_nodes: number
  labeled_histories: number
  dataset_built_nodes: number
  trained_runs: number
  deployed_models: number
}

export interface MonthlyCount {
  period: string // YYYY-MM
  manual_count: number
  auto_count: number
  total_count: number
}

export interface TestDatasetSummary {
  has_permission: boolean
  has_data: boolean
  total_nodes: number
  ready_count: number
  recent_7d_datasets: number
}

export interface DashboardSummaryResponse {
  raw: RawSummary
  label: LabelSummary
  dataset: DatasetSummary
  test_dataset?: TestDatasetSummary | null
  training: TrainingSummary
  deployment: DeploymentSummary
  pipeline: PipelineSummary
  raw_monthly_uploads: MonthlyCount[]
}

export interface UploadActivity {
  id: number
  project_id: number
  node_id: number
  source_filename: string
  status: string
  upload_method?: string | null
  started_at?: string | null
  completed_at?: string | null
  minutes_ago?: number | null
}

export interface LabelSetActivity {
  id: number
  version_node_id: number
  project_name?: string | null
  version_name?: string | null
  status: string
  total_labels: number
  created_at?: string | null
}

export interface DatasetJobActivity {
  id: number
  dataset_node_id: number
  project_name?: string | null
  dataset_name?: string | null
  status: string
  progress?: number | null
  created_at?: string | null
}

export interface TrainRunActivity {
  id: number
  task_name?: string | null
  project_name?: string | null
  status: string
  is_released: boolean
  created_at?: string | null
}

export interface DeploymentActivity {
  id: number
  train_run_id: number
  version_name?: string | null
  deployed_at?: string | null
  created_at?: string | null
}

export interface IngestQualityActivity {
  id: number
  reason: string
  reason_label?: string | null
  message?: string | null
  trigger_id?: string | null
  event_at?: string | null
}

export interface AutoIngestRecentBatch {
  id: number
  project_name?: string | null
  node_name?: string | null
  status: string
  item_count: number
  minutes_ago?: number | null
}

export interface AnonymizationHealth {
  canary_status?: 'ok' | 'failed' | 'not_configured' | null
  canary_checked_at?: string | null
  canary_error?: string | null
  quarantined_count: number
  quarantined_oldest_at?: string | null
  quarantined_exhausted?: number
}

export interface AutoIngestStatus {
  in_progress: boolean
  processing_count: number
  pending_count: number
  recent_1h_files: number
  recent_batches: AutoIngestRecentBatch[]  // 최근 7일 안의 회차
  last_ingest_minutes_ago?: number | null  // 기간과 무관한 마지막 유입 — 목록이 비었을 때 표시
  recent_failures: IngestQualityActivity[]
  anonymization?: AnonymizationHealth | null
}

export interface DashboardMyActivitiesResponse {
  scope: 'personal' | 'team' | 'org'
  recent_uploads: UploadActivity[]  // 최근 7일 안의 내 수동 업로드
  last_upload_minutes_ago?: number | null  // 기간과 무관한 마지막 업로드 — 목록이 비었을 때 표시
  recent_label_sets: LabelSetActivity[]
  recent_dataset_jobs: DatasetJobActivity[]
  recent_train_runs: TrainRunActivity[]
  recent_deployments: DeploymentActivity[]
  can_view_auto_ingest: boolean
  auto_ingest?: AutoIngestStatus | null
}

class DashboardService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async getSummary(year?: number): Promise<DashboardSummaryResponse> {
    const params = new URLSearchParams()
    if (year) params.set('year', String(year))
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const resp = await fetch(`${this.baseUrl}/dashboard/summary${suffix}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      throw new Error(`대시보드 요약 조회 실패: ${resp.status}`)
    }
    return resp.json()
  }

  async getMyActivities(limit: number = 5): Promise<DashboardMyActivitiesResponse> {
    const params = new URLSearchParams({ limit: String(limit) })
    const resp = await fetch(`${this.baseUrl}/dashboard/my-activities?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      throw new Error(`대시보드 활동 조회 실패: ${resp.status}`)
    }
    return resp.json()
  }
}

export default new DashboardService()


